import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Statistic, Button, Space, message, Modal, Select } from 'antd';
import { ReloadOutlined, PlusOutlined, SettingOutlined, WarningOutlined, MailOutlined, ThunderboltOutlined } from '@ant-design/icons';
import UldStockMonitorTable from './components/UldStockMonitorTable';
import UldAlertConfigDrawer from './components/UldAlertConfigDrawer';
import { readApiErrorMessage } from './readApiError';
import { uldStockAlertService, masterDataService } from '../../../api/enhancedApi';
import './UldStockAlertPage.css';

const AIRPORT_PAGE_SIZE = 500;
const AIRPORT_PAGE_CAP = 40;

const readAirportPage = (response) => {
    const payload = response?.data ?? response;
    if (Array.isArray(payload)) {
        return { items: payload, total: payload.length };
    }
    const items = Array.isArray(payload?.items) ? payload.items : [];
    const total = Number.isFinite(payload?.total) ? payload.total : items.length;
    return { items, total };
};

const UldStockAlertPage = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [stats, setStats] = useState({
        totalMonitored: 0,
        warningCount: 0,
        emailEnabledCount: 0,
        unconfiguredCount: 0,
    });

    const [drawerVisible, setDrawerVisible] = useState(false);
    const [currentAirport, setCurrentAirport] = useState(null);

    const [addModalVisible, setAddModalVisible] = useState(false);
    const [allAirports, setAllAirports] = useState([]);
    const [selectedAirports, setSelectedAirports] = useState([]);
    const [adding, setAdding] = useState(false);
    const [airportsLoading, setAirportsLoading] = useState(false);
    const [triggering, setTriggering] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await uldStockAlertService.getMonitorList();
            const list = Array.isArray(response) ? response : (response.data || []);
            const rows = Array.isArray(list) ? list : [];
            const configured = rows.filter((item) => item.config_id);

            setData(rows);
            setStats({
                totalMonitored: configured.length,
                warningCount: rows.filter((item) => item.alert_status === 'WARNING').length,
                emailEnabledCount: configured.filter((item) => item.is_email_enabled && item.min_stock_threshold > 0).length,
                unconfiguredCount: rows.length - configured.length,
            });
        } catch (error) {
            console.error('获取监控列表失败:', error);
            message.error(readApiErrorMessage(error, '获取数据失败'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const fetchAllAirports = async () => {
        setAirportsLoading(true);
        try {
            const collected = [];
            let total = Infinity;
            let page = 1;
            while (collected.length < total && page <= AIRPORT_PAGE_CAP) {
                const response = await masterDataService.airport.getAirportList({
                    page,
                    page_size: AIRPORT_PAGE_SIZE,
                    is_active: true,
                });
                const airportPage = readAirportPage(response);
                total = airportPage.total;
                collected.push(...airportPage.items);
                if (airportPage.items.length < AIRPORT_PAGE_SIZE) break;
                page += 1;
            }
            setAllAirports(collected);
            if (collected.length < total) {
                message.warning(`机场名单只加载了前 ${collected.length} 个，后面的机场这次选不到`);
            }
        } catch (error) {
            console.error('获取机场列表失败:', error);
            message.error(readApiErrorMessage(error, '无法加载机场列表'));
        } finally {
            setAirportsLoading(false);
        }
    };

    const handleEdit = (record) => {
        setCurrentAirport(record);
        setDrawerVisible(true);
    };

    const handleRemove = async (record) => {
        try {
            if (record.config_id) {
                await uldStockAlertService.removeMonitor(record.config_id);
            } else {
                await uldStockAlertService.hideAirport({ airport_iata: record.airport_iata });
            }
            message.success('已从列表移除');
            fetchData();
        } catch (error) {
            console.error('移除失败:', error);
            message.error(readApiErrorMessage(error, '移除失败'));
        }
    };

    const openAddModal = () => {
        if (allAirports.length === 0) {
            fetchAllAirports();
        }
        setSelectedAirports([]);
        setAddModalVisible(true);
    };

    const handleAddMonitor = async () => {
        if (selectedAirports.length === 0) {
            message.warning('请至少选择一个机场');
            return;
        }

        setAdding(true);
        try {
            const settled = await Promise.all(selectedAirports.map(async (iata) => {
                try {
                    const response = await uldStockAlertService.addMonitor({ airport_iata: iata });
                    const config = response?.data || {};
                    const keptSettings = (Array.isArray(config.recipients) && config.recipients.length > 0) || Number(config.min_stock_threshold) > 0;
                    return { iata, ok: true, emailOffWithSettings: keptSettings && !config.is_email_enabled };
                } catch (error) {
                    return { iata, ok: false, reason: readApiErrorMessage(error, '未知错误') };
                }
            }));
            const successList = settled.filter((item) => item.ok);
            const failedList = settled.filter((item) => !item.ok);
            const restored = successList.filter((item) => item.emailOffWithSettings);
            const restoredNote = restored.length
                ? `${restored.map((item) => item.iata).join("、")} 保留了原来的阈值和收件人，邮件开关仍是关闭的，要发信请再打开。`
                : "";

            if (failedList.length === 0) {
                if (restoredNote) {
                    message.warning(`已加入 ${successList.length} 个站点。${restoredNote}`);
                } else {
                    message.success(`成功添加 ${successList.length} 个监控站点`);
                }
                setAddModalVisible(false);
                fetchData();
            } else if (successList.length > 0) {
                message.warning(`成功添加 ${successList.length} 个，失败 ${failedList.length} 个。失败站点: ${failedList.map((item) => item.iata).join(', ')}${restoredNote ? ` ${restoredNote}` : ""}`);
                setSelectedAirports(failedList.map((item) => item.iata));
                fetchData();
            } else {
                message.error(`所有站点添加失败: ${failedList[0]?.reason || '添加失败'}`);
            }
        } catch (error) {
            console.error('添加监控异常:', error);
            message.error(readApiErrorMessage(error, '添加监控时发生异常，请重试'));
        } finally {
            setAdding(false);
        }
    };

    const handleTriggerCheck = () => {
        if (triggering) return;
        Modal.confirm({
            title: '立即发送预警邮件？',
            content: '将忽略 7 小时冷却期。只会发给已经打开邮件、填了收件人、并且最低库存大于 0 的短缺站点。列表里标红但没开邮件的站点不会收到。两分钟内刚发过的站点不会再发一封。',
            okText: '发送',
            cancelText: '取消',
            okButtonProps: { danger: true },
            onOk: async () => {
                setTriggering(true);
                try {
                    const response = await uldStockAlertService.triggerAlertCheck(true);
                    const body = response?.data || {};
                    const text = body.message || '预警检查已完成';
                    const sent = body.data?.sent_count || 0;
                    const failed = body.data?.failed_count || 0;
                    if (failed > 0) {
                        message.error(text);
                    } else if (sent > 0) {
                        message.success(text);
                    } else {
                        message.warning(text);
                    }
                    fetchData();
                } catch (error) {
                    console.error('触发预警检查失败:', error);
                    const timedOut = error?.code === "ECONNABORTED" || String(error?.message || "").toLowerCase().includes("timeout");
                    if (timedOut) {
                        message.warning("页面等待超时。邮件可能已经发出，请先刷新看最近预警时间。两分钟内刚发过的站点不会再发一封。");
                        fetchData();
                    } else {
                        message.error(readApiErrorMessage(error, '触发预警检查失败'));
                    }
                } finally {
                    setTriggering(false);
                }
            },
        });
    };

    const configuredIatas = new Set(data.filter((item) => item.config_id).map((item) => item.airport_iata));
    const airportOptions = allAirports
        .filter((airport) => airport.iata_code && !configuredIatas.has(airport.iata_code))
        .map((airport) => ({
            value: airport.iata_code,
            label: `${airport.iata_code} - ${airport.airport_name || ''} ${airport.city || ''}`,
        }));

    return (
        <div className="uld-stock-alert-page">
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={8}>
                    <Card>
                        <Statistic
                            title="已配置监控"
                            value={stats.totalMonitored}
                            prefix={<SettingOutlined />}
                        />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card>
                        <Statistic
                            title="库存预警站点"
                            value={stats.warningCount}
                            valueStyle={{ color: '#cf1322' }}
                            prefix={<WarningOutlined />}
                        />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card>
                        <Statistic
                            title="会发邮件的站点"
                            value={stats.emailEnabledCount}
                            prefix={<MailOutlined />}
                        />
                    </Card>
                </Col>
            </Row>
            {stats.unconfiguredCount > 0 ? (
                <div className="uld-stock-alert-unconfigured-note">
                    另外 {stats.unconfiguredCount} 个机场现在有可用板，但还没配置预警，所以没有算进「已配置监控」，也不会发邮件。不需要看的，可以在列表里移除。
                </div>
            ) : null}

            <Card
                title="库存监控列表"
                extra={
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={fetchData}>
                            刷新
                        </Button>
                        <Button
                            icon={<ThunderboltOutlined />}
                            onClick={handleTriggerCheck}
                            loading={triggering}
                            danger
                            title="忽略 7 小时冷却期。只发给已开启邮件、且库存低于阈值的站点"
                        >
                            立即发送预警
                        </Button>
                        <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}>
                            添加监控站点
                        </Button>
                    </Space>
                }
            >
                <UldStockMonitorTable
                    loading={loading}
                    data={data}
                    onEdit={handleEdit}
                    onRemove={handleRemove}
                />
            </Card>

            <UldAlertConfigDrawer
                visible={drawerVisible}
                onClose={() => setDrawerVisible(false)}
                airportData={currentAirport}
                onSuccess={fetchData}
            />

            <Modal
                title="添加监控站点"
                open={addModalVisible}
                onOk={handleAddMonitor}
                onCancel={() => setAddModalVisible(false)}
                confirmLoading={adding}
                width={600}
            >
                <p>选择要配置预警的机场。只是手头有板、还没配置的机场也会出现在列表里，但不会发邮件。不需要看的，可以在列表里移除。</p>
                <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    placeholder="搜索并选择机场 (支持 IATA 代码或名称)"
                    value={selectedAirports}
                    onChange={setSelectedAirports}
                    loading={airportsLoading}
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={airportOptions}
                    notFoundContent={airportsLoading ? '正在加载机场…' : '没有可添加的机场'}
                />
            </Modal>
        </div>
    );
};

export default UldStockAlertPage;
